import Descriptions from 'antd/lib/descriptions';
import React from 'react';
import { appleReleaseTypeForReleaseMethod, releaseSettingsNeedPhasedReleaseWarning } from '../../helpers';
import { displayVersionReleaseType } from '../../helpers/displayHelpers';
import { AppleAppStoreDefaultSettings, AppleAppStoreData } from '../../types';
import { antGrey, runwayColors } from '../styles/globalStyles';
import Space from 'antd/lib/space';
import { WarningFilled, CheckCircleFilled } from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import { AppComponentProps, Nullable, StepRunComponentProps } from '../../typescriptHelpers';
import { useAppPlatform } from '@/utils/hooks/helpers';

interface AppleAppStoreReleaseSettingsDescriptionsProps extends AppComponentProps {
    appDefaultSettings?: AppleAppStoreDefaultSettings;
    appStoreReleaseSettings?: AppleAppStoreDefaultSettings;
    appStoreData?: AppleAppStoreData;
    isRollback: boolean;
    isRunwayReleaseAutomationInactiveBecauseOfApple: boolean;
    willBeReleasedByRunway: boolean;
    labelStyle?: React.CSSProperties;
    contentStyle?: React.CSSProperties;
    colon?: boolean;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
}

const AppleAppStoreReleaseSettingsDescriptions: React.FC<AppleAppStoreReleaseSettingsDescriptionsProps> = (
    props: AppleAppStoreReleaseSettingsDescriptionsProps,
) => {
    const {
        appStoreData,
        appStoreReleaseSettings,
        willBeReleasedByRunway,
        isRunwayReleaseAutomationInactiveBecauseOfApple,
    } = props;

    const platform = useAppPlatform(props);

    let isPhasedRelease = false;
    let releaseType = null;
    let earliestReleaseDate = null;

    if (props.stepRunComponentProps) {
        isPhasedRelease = appStoreReleaseSettings.phasedRelease;
        releaseType = appleReleaseTypeForReleaseMethod(
            (appStoreReleaseSettings as AppleAppStoreDefaultSettings).releaseMethod,
        );
    } else if (appStoreData || appStoreReleaseSettings) {
        isPhasedRelease =
            appStoreData ? !!appStoreData.phasedRelease
            : appStoreReleaseSettings ? (appStoreReleaseSettings as AppleAppStoreDefaultSettings).phasedRelease
            : false;
        releaseType =
            appStoreData ? appStoreData.versionReviewSubmissionItem?.releaseType
            : appStoreReleaseSettings ?
                appleReleaseTypeForReleaseMethod(
                    (appStoreReleaseSettings as AppleAppStoreDefaultSettings).releaseMethod,
                )
            :   null;
    }

    earliestReleaseDate = appStoreData ? appStoreData.versionReviewSubmissionItem?.earliestReleaseDate : null;

    let phasedReleaseText =
        isPhasedRelease ? 'On (release will be phased over 7 days)' : 'Off (release update to all users immediately)';

    // warn if app release phased settings differ from app default phased settings
    const needsPhasedReleaseWarning = releaseSettingsNeedPhasedReleaseWarning(
        platform,
        props.isRollback,
        props.appDefaultSettings,
        appStoreReleaseSettings,
    );

    return (
        <Descriptions colon={props.colon} column={1} labelStyle={props.labelStyle} contentStyle={props.contentStyle}>
            <Descriptions.Item label="Version release">
                {releaseType ?
                    <Space direction="vertical">
                        {displayVersionReleaseType(releaseType, earliestReleaseDate)}
                        {isRunwayReleaseAutomationInactiveBecauseOfApple && (
                            <Space align="start">
                                <WarningFilled style={{ color: runwayColors.statusYellow }} />
                                <div>
                                    <Text style={{ color: runwayColors.statusYellow, fontWeight: 600 }}>
                                        App Store Connect override:{' '}
                                    </Text>
                                    <Text style={{ color: antGrey.grey7 }}>
                                        Runway is unable to perform your scheduled release automation because the
                                        version release setting is not set to Manual. Change this setting to Manual to
                                        allow Runway to release automatically as expected.
                                    </Text>
                                </div>
                            </Space>
                        )}
                        {willBeReleasedByRunway && !isRunwayReleaseAutomationInactiveBecauseOfApple && (
                            <Space align="start">
                                <CheckCircleFilled style={{ color: runwayColors.statusGreen }} />
                                <Text style={{ color: antGrey.grey7 }}>
                                    Keep this set to Manual in App Store Connect in order for Runway to run your release
                                    automation.
                                </Text>
                            </Space>
                        )}
                    </Space>
                :   <Text type="secondary">Not set </Text>}
            </Descriptions.Item>
            <Descriptions.Item label="Phased release">
                <Space direction="vertical">
                    {phasedReleaseText ? phasedReleaseText : <Text type="secondary">Not set</Text>}
                    {needsPhasedReleaseWarning && (
                        <Space align="start">
                            <WarningFilled style={{ color: runwayColors.statusYellow }} />
                            <Text style={{ color: antGrey.grey7 }}>
                                Phased release is off for this release because it&apos;s a rollback.
                            </Text>
                        </Space>
                    )}
                </Space>
            </Descriptions.Item>
        </Descriptions>
    );
};

export default AppleAppStoreReleaseSettingsDescriptions;
